/* eslint-disable consistent-return */
/**
 * Custom hook for managing scrollable content detection
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { isContentScrollable } from '../utils';

interface UseScrollableContentProps {
  enabled?: boolean;
  debounceMs?: number;
}

interface UseScrollableContentReturn {
  isScrollable: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
  checkOverflow: () => void;
}

const useScrollableContent = ({
  enabled = true,
  debounceMs = 100,
}: UseScrollableContentProps = {}): UseScrollableContentReturn => {
  const [isScrollable, setIsScrollable] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkOverflow = useCallback(() => {
    if (!enabled || !contentRef.current) {
      return;
    }

    const element = contentRef.current;
    const scrollable = isContentScrollable(element);

    setIsScrollable(scrollable);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Initial check
    checkOverflow();

    // Debounced resize handler
    let timeoutId: number | undefined;

    const handleResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(checkOverflow, debounceMs);
    };

    // Add event listeners
    window.addEventListener('resize', handleResize);

    // Use ResizeObserver if available for more precise detection
    let resizeObserver: ResizeObserver | null = null;

    if (typeof window !== 'undefined' && contentRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [enabled, checkOverflow, debounceMs]);

  return {
    isScrollable,
    contentRef,
    checkOverflow,
  };
};

export default useScrollableContent;
